import { useContext, useState, useRef } from "react";
import { Grid } from "@mui/material";
import ChatConversationLayout from "./ChatConversationLayout";
import ChatInputLayout from "./ChatInputLayout";
import ChatAppBar from "./ChatAppBar";
import { createChat, getSuggestions, sendPrompt } from "../../services/Chat";
import { ACTIONS, WizardContext } from "../wizard/Context";
import { useMutation, useQuery, useQueryClient } from "react-query";

const ChatLayout = ({
    isFullScreen,
    position,
    setOffset,
    isDragging,
    setIsDragging,
    handleNewChat,
    userDetails,
}) => {
    const { state, dispatch } = useContext(WizardContext);
    const { chatConfig } = state;
    const queryClient = useQueryClient();
    const [refresh, setRefresh] = useState(false);

    // Query to fetch chat suggestions
    const { data: suggestions, refetch } = useQuery(
        ['chatSuggestions', chatConfig?.chatId, refresh],
        getSuggestions,
        {
            enabled: !refresh,
            refetchOnWindowFocus: false,
        }
    );

    // Mutation to send prompt
    const { mutate, isLoading } = useMutation(
        sendPrompt,
        {
            mutationKey: ['sendPrompt', chatConfig?.chatId],
            onSuccess: (conversation) => {
                const previousData = queryClient.getQueryData(["getChatById", conversation?.chatId]);

                queryClient.setQueryData(["getChatById", conversation?.chatId], (previousData) => ({
                    ...previousData,
                    pages: previousData.pages.map((page, index) => {
                        if (index === 0) {
                            return {
                                ...page,
                                conversations: [...page.conversations, conversation?.response],
                            };
                        }
                        return page;
                    })
                }));

                if (chatConfig?.initiated) queryClient.invalidateQueries("getChats");
                refetch();

                return { previousData };
            },
            onMutate: (conversation) => {
                const previousData = queryClient.getQueryData(["getChatById", conversation?.chatId]);

                if (previousData) {
                    queryClient.setQueryData(["getChatById", conversation?.chatId], (previousData) => ({
                        ...previousData,
                        pages: previousData.pages.map((page, index) => {
                            if (index === 0) {
                                return {
                                    ...page,
                                    conversations: [...page.conversations, conversation?.prompt],
                                };
                            }
                            return page;
                        })
                    }));
                } else {
                    queryClient.setQueryData(["getChatById", conversation?.chatId], () => ({
                        pageParams: [],
                        pages: [{
                            chatId: conversation?.chatId,
                            conversations: [conversation?.prompt],
                        }],
                    }));

                    queryClient.removeQueries(["getChatById", "0"]);
                }

                return { previousData };
            }
        }
    );

    const handleSendPrompt = async (inputText) => {
        const prompt = { role: 'user', text: inputText, timestamp: new Date().toISOString() };
        if (!refresh) setRefresh(true);

        if (!chatConfig?.chatId) {
            queryClient.setQueryData(["getChatById", "0"], () => ({
                pages: [{
                    chatId: "0",
                    conversations: [prompt],
                }],
            }));

            const res = await createChat({
                userId: userDetails?.userId,
                title: inputText,
            });

            dispatch({
                type: ACTIONS.UPDATE_CHAT_WINDOW,
                payload: {
                    chatConfig: {
                        ...chatConfig,
                        chatId: res?.chatId,
                        title: inputText,
                        createdAt: res?.createdAt,
                    },
                },
            });

            mutate({
                userId: userDetails?.userId,
                chatId: res?.chatId,
                prompt,
            });
        } else {
            mutate({
                userId: userDetails?.userId,
                chatId: chatConfig?.chatId,
                prompt,
            });
        }
    }

    return (
        <Grid
            sx={{
                height: 'inherit',
            }}
            size='grow'
            container>

            <ChatAppBar
                isFullScreen={isFullScreen}
                position={position}
                setOffset={setOffset}
                isDragging={isDragging}
                setIsDragging={setIsDragging}
                handleNewChat={handleNewChat}
            />
            <ChatConversationLayout
                isFullScreen={isFullScreen}
                userDetails={userDetails}
                isLoading={isLoading}
            />

            <ChatInputLayout
                isFullScreen={isFullScreen}
                handleSendPrompt={handleSendPrompt}
                isLoading={isLoading}
                suggestions={suggestions?.faqSuggestions}
            />
        </Grid>
    );
}

export default ChatLayout;